<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Spinner from 'shared/components/Spinner.vue';
import JSZip from 'jszip';

export default {
  name: 'DocumentsTab',
  components: {
    Spinner,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    contact: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const attachments = ref([]);
    const isLoading = ref(false);
    const selectedAttachment = ref(null);

    // Get account ID for API calls
    const accountId = computed(() => store.getters.getCurrentAccountId);

    // Selection state
    const selectedIds = ref(new Set());
    const isDownloading = ref(false);

    // Pagination
    const itemsPerPage = 10;
    const sentPage = ref(1);
    const receivedPage = ref(1);
    const otherPage = ref(1);

    // Audio player
    const currentlyPlaying = ref(null);
    const audioElement = ref(null);

    const contactPhone = computed(() => props.contact?.phone_number || props.contact?.identifier || '');

    const sortedAttachments = computed(() => {
      return [...attachments.value].sort((a, b) => {
        // created_at is Unix timestamp in seconds, higher = more recent
        return (b.created_at || 0) - (a.created_at || 0);
      });
    });

    // Separate sent and received attachments
    // message_type can be: 0/incoming (received from contact), 1/outgoing (sent by agent)
    const sentAttachments = computed(() => {
      return sortedAttachments.value.filter(a => {
        const type = a.message_type;
        return type === 1 || type === '1' || type === 'outgoing';
      });
    });

    const receivedAttachments = computed(() => {
      return sortedAttachments.value.filter(a => {
        const type = a.message_type;
        return type === 0 || type === '0' || type === 'incoming';
      });
    });

    // Attachments without a valid message_type (fallback)
    const otherAttachments = computed(() => {
      return sortedAttachments.value.filter(a => {
        const type = a.message_type;
        return type !== 0 && type !== '0' && type !== 'incoming' &&
               type !== 1 && type !== '1' && type !== 'outgoing';
      });
    });

    // Paginated attachments
    const paginatedSent = computed(() => {
      const start = (sentPage.value - 1) * itemsPerPage;
      return sentAttachments.value.slice(start, start + itemsPerPage);
    });

    const paginatedReceived = computed(() => {
      const start = (receivedPage.value - 1) * itemsPerPage;
      return receivedAttachments.value.slice(start, start + itemsPerPage);
    });

    const paginatedOther = computed(() => {
      const start = (otherPage.value - 1) * itemsPerPage;
      return otherAttachments.value.slice(start, start + itemsPerPage);
    });

    // Total pages
    const sentTotalPages = computed(() => Math.ceil(sentAttachments.value.length / itemsPerPage));
    const receivedTotalPages = computed(() => Math.ceil(receivedAttachments.value.length / itemsPerPage));
    const otherTotalPages = computed(() => Math.ceil(otherAttachments.value.length / itemsPerPage));

    const fetchAttachments = async () => {
      isLoading.value = true;
      try {
        const response = await ConversationApi.getAllAttachments(props.conversationId);
        attachments.value = response.data.payload || [];
        // Debug: log first attachment to see message_type format
        if (attachments.value.length > 0) {
          console.log('First attachment message_type:', attachments.value[0].message_type, typeof attachments.value[0].message_type);
        }
      } catch (error) {
        console.error('Error loading attachments:', error);
        attachments.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return '-';
      // API returns Unix timestamp in seconds, JavaScript needs milliseconds
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const getFileIcon = (fileType) => {
      const icons = {
        image: 'i-lucide-image',
        video: 'i-lucide-video',
        audio: 'i-lucide-music',
        file: 'i-lucide-file-text',
        location: 'i-lucide-map-pin',
      };
      return icons[fileType] || icons.file;
    };

    const getFileIconClass = (fileType) => {
      const classes = {
        image: 'text-blue-500',
        video: 'text-purple-500',
        audio: 'text-green-500',
        file: 'text-red-500',
        location: 'text-orange-500',
      };
      return classes[fileType] || classes.file;
    };

    const isPdf = (attachment) => {
      const extension = attachment.extension || '';
      const dataUrl = attachment.data_url || '';
      return extension.toLowerCase() === 'pdf' || dataUrl.toLowerCase().includes('.pdf');
    };

    const isImage = (attachment) => {
      return attachment.file_type === 'image';
    };

    const isVideo = (attachment) => {
      return attachment.file_type === 'video';
    };

    const isAudio = (attachment) => {
      return attachment.file_type === 'audio';
    };

    // message_type: 0 = incoming (received), 1 = outgoing (sent)
    const isIncoming = (attachment) => {
      return attachment.message_type === 0;
    };

    const isOutgoing = (attachment) => {
      return attachment.message_type === 1;
    };

    const getDirectionLabel = (attachment) => {
      return isIncoming(attachment) ? t('DOCUMENTS.RECEIVED') : t('DOCUMENTS.SENT');
    };

    const getDirectionClass = (attachment) => {
      return isIncoming(attachment)
        ? 'bg-blue-500 text-white'
        : 'bg-emerald-500 text-white';
    };

    const openAttachment = (attachment) => {
      // Don't open audio files - they use the player
      if (attachment.file_type === 'audio') {
        return;
      }
      if (attachment.data_url) {
        window.open(attachment.data_url, '_blank');
      }
    };

    const toggleAudio = (attachment) => {
      if (currentlyPlaying.value === attachment.id) {
        // Pause current audio
        if (audioElement.value) {
          audioElement.value.pause();
        }
        currentlyPlaying.value = null;
      } else {
        // Stop any currently playing audio
        if (audioElement.value) {
          audioElement.value.pause();
        }
        // Play new audio
        audioElement.value = new Audio(attachment.data_url);
        audioElement.value.play();
        currentlyPlaying.value = attachment.id;

        // Reset when audio ends
        audioElement.value.onended = () => {
          currentlyPlaying.value = null;
        };
      }
    };

    const isPlaying = (attachment) => {
      return currentlyPlaying.value === attachment.id;
    };

    // Selection functions
    const isSelected = (attachment) => {
      return selectedIds.value.has(attachment.id);
    };

    const toggleSelection = (attachment, event) => {
      event.stopPropagation();
      const newSet = new Set(selectedIds.value);
      if (newSet.has(attachment.id)) {
        newSet.delete(attachment.id);
      } else {
        newSet.add(attachment.id);
      }
      selectedIds.value = newSet;
    };

    const selectAll = () => {
      const allIds = sortedAttachments.value.map(a => a.id);
      selectedIds.value = new Set(allIds);
    };

    const deselectAll = () => {
      selectedIds.value = new Set();
    };

    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        deselectAll();
      } else {
        selectAll();
      }
    };

    const isAllSelected = computed(() => {
      return sortedAttachments.value.length > 0 &&
             selectedIds.value.size === sortedAttachments.value.length;
    });

    const selectedCount = computed(() => {
      return selectedIds.value.size;
    });

    const hasSelection = computed(() => {
      return selectedIds.value.size > 0;
    });

    // Build proxy URL for downloading attachment through backend (avoids CORS issues)
    const getProxyDownloadUrl = (attachmentId) => {
      return `/api/v1/accounts/${accountId.value}/conversations/${props.conversationId}/attachments/${attachmentId}/download`;
    };

    // Download as ZIP
    const downloadAsZip = async () => {
      if (selectedIds.value.size === 0) return;

      isDownloading.value = true;
      let filesAdded = 0;

      try {
        const zip = new JSZip();
        const selectedAttachments = sortedAttachments.value.filter(a => selectedIds.value.has(a.id));

        // Download each file through backend proxy and add to ZIP
        const downloadPromises = selectedAttachments.map(async (attachment, index) => {
          try {
            // Use backend proxy to avoid CORS issues
            // Use global axios which has authentication configured
            const proxyUrl = getProxyDownloadUrl(attachment.id);
            const response = await window.axios.get(proxyUrl, {
              responseType: 'blob',
            });

            const blob = response.data;

            // Check if blob has content
            if (!blob || blob.size === 0) {
              console.error(`Empty blob for ${attachment.file_name}`);
              return;
            }

            // Map content-type to file extension
            const mimeToExt = {
              'image/jpeg': 'jpg',
              'image/jpg': 'jpg',
              'image/png': 'png',
              'image/gif': 'gif',
              'image/webp': 'webp',
              'image/svg+xml': 'svg',
              'video/mp4': 'mp4',
              'video/webm': 'webm',
              'video/quicktime': 'mov',
              'audio/mpeg': 'mp3',
              'audio/mp3': 'mp3',
              'audio/ogg': 'ogg',
              'audio/wav': 'wav',
              'audio/webm': 'webm',
              'application/pdf': 'pdf',
              'application/msword': 'doc',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
              'application/vnd.ms-excel': 'xls',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
              'text/plain': 'txt',
              'text/csv': 'csv',
            };

            // Generate filename with proper extension
            let fileName = attachment.file_name || `file_${index + 1}`;

            // Get extension from various sources
            let extension = attachment.extension;

            // If no extension in filename, try to get from blob content-type
            if (!fileName.includes('.')) {
              if (!extension && blob.type) {
                extension = mimeToExt[blob.type] || blob.type.split('/')[1];
              }
              // Also try from file_type
              if (!extension && attachment.file_type) {
                const typeMap = { image: 'jpg', video: 'mp4', audio: 'mp3', file: 'bin' };
                extension = typeMap[attachment.file_type];
              }
              if (extension) {
                fileName += `.${extension}`;
              }
            }

            // Add prefix to avoid duplicates
            const timestamp = attachment.created_at || Date.now();
            const uniqueName = `${timestamp}_${fileName}`;

            zip.file(uniqueName, blob);
            filesAdded++;
          } catch (error) {
            console.error(`Error downloading ${attachment.file_name}:`, error);
          }
        });

        await Promise.all(downloadPromises);

        // Check if any files were added
        if (filesAdded === 0) {
          alert(t('DOCUMENTS.DOWNLOAD_ERROR') || 'Erro ao baixar arquivos. Nenhum arquivo pôde ser baixado.');
          return;
        }

        // Generate and download ZIP
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const contactName = props.contact?.name || props.contact?.phone_number || 'documentos';
        const zipFileName = `${contactName}_documentos.zip`;

        // Create download link
        const link = document.createElement('a');
        link.href = URL.createObjectURL(zipBlob);
        link.download = zipFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        // Clear selection after download
        deselectAll();
      } catch (error) {
        console.error('Error creating ZIP:', error);
        alert(t('DOCUMENTS.DOWNLOAD_ERROR') || 'Erro ao baixar arquivos');
      } finally {
        isDownloading.value = false;
      }
    };

    watch(() => props.conversationId, (newId) => {
      if (newId) {
        fetchAttachments();
        deselectAll();
      }
    });

    onMounted(() => {
      fetchAttachments();
    });

    return {
      t,
      attachments,
      sortedAttachments,
      sentAttachments,
      receivedAttachments,
      otherAttachments,
      paginatedSent,
      paginatedReceived,
      paginatedOther,
      sentPage,
      receivedPage,
      otherPage,
      sentTotalPages,
      receivedTotalPages,
      otherTotalPages,
      itemsPerPage,
      isLoading,
      selectedAttachment,
      contactPhone,
      formatDate,
      getFileIcon,
      getFileIconClass,
      isPdf,
      isImage,
      isVideo,
      isAudio,
      isIncoming,
      isOutgoing,
      getDirectionLabel,
      getDirectionClass,
      openAttachment,
      toggleAudio,
      isPlaying,
      currentlyPlaying,
      // Selection
      selectedIds,
      isSelected,
      toggleSelection,
      toggleSelectAll,
      isAllSelected,
      selectedCount,
      hasSelection,
      isDownloading,
      downloadAsZip,
    };
  },
};
</script>

<template>
  <div class="documents-tab h-full flex flex-col bg-white dark:bg-slate-900">
    <!-- Header -->
    <div class="p-4 border-b border-slate-200 dark:border-slate-700">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {{ t('DOCUMENTS.TITLE') }}
          </h2>
          <p class="text-sm text-slate-500">
            {{ sortedAttachments.length }} {{ t('DOCUMENTS.FILES_COUNT') }}
          </p>
        </div>
        <!-- Selection controls -->
        <div v-if="sortedAttachments.length > 0" class="flex items-center gap-3 flex-wrap">
          <label class="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              :checked="isAllSelected"
              class="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              @change="toggleSelectAll"
            />
            <span class="text-sm text-slate-600 dark:text-slate-400">
              {{ t('DOCUMENTS.SELECT_ALL') }}
            </span>
          </label>
          <span v-if="selectedCount > 0" class="text-sm text-slate-500">
            ({{ selectedCount }} {{ t('DOCUMENTS.SELECTED') }})
          </span>
        </div>
      </div>
      <!-- Download ZIP button - separate row for visibility -->
      <div v-if="hasSelection" class="mt-3 flex justify-end">
        <button
          :disabled="isDownloading"
          class="download-zip-btn flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
          @click="downloadAsZip"
        >
          <svg v-if="isDownloading" class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          {{ isDownloading ? t('DOCUMENTS.DOWNLOADING') : t('DOCUMENTS.DOWNLOAD_ZIP') }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto p-4">
      <!-- Loading -->
      <div v-if="isLoading" class="flex justify-center items-center h-full">
        <Spinner />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="sortedAttachments.length === 0"
        class="flex flex-col items-center justify-center py-12 text-center"
      >
        <div class="w-16 h-16 mb-4 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
          <span class="i-lucide-folder-open text-3xl text-slate-400" />
        </div>
        <h3 class="text-lg font-medium text-slate-700 dark:text-slate-200 mb-2">
          {{ t('DOCUMENTS.EMPTY.TITLE') }}
        </h3>
        <p class="text-sm text-slate-500">
          {{ t('DOCUMENTS.EMPTY.MESSAGE') }}
        </p>
      </div>

      <!-- Documents Sections -->
      <div v-else class="space-y-8">
        <!-- Sent Section -->
        <div v-if="sentAttachments.length > 0">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span class="i-lucide-arrow-up-right text-emerald-500 text-xl" />
              <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">
                {{ t('DOCUMENTS.SENT') }}
              </h3>
              <span class="text-sm text-slate-500">({{ sentAttachments.length }})</span>
            </div>
            <!-- Pagination Controls -->
            <div v-if="sentTotalPages > 1" class="flex items-center gap-2">
              <button
                :disabled="sentPage === 1"
                class="px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700"
                @click="sentPage--"
              >
                <span class="i-lucide-chevron-left" />
              </button>
              <span class="text-sm text-slate-600 dark:text-slate-400">
                {{ sentPage }} / {{ sentTotalPages }}
              </span>
              <button
                :disabled="sentPage === sentTotalPages"
                class="px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700"
                @click="sentPage++"
              >
                <span class="i-lucide-chevron-right" />
              </button>
            </div>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <div
              v-for="(attachment, index) in paginatedSent"
              :key="attachment.id"
              class="document-card bg-white dark:bg-slate-800 rounded-lg border-2 transition-all cursor-pointer hover:shadow-lg"
              :class="isSelected(attachment) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-emerald-200 dark:border-emerald-800 hover:border-emerald-400'"
              @click="openAttachment(attachment)"
            >
              <!-- Preview Area -->
              <div class="relative aspect-square flex items-center justify-center bg-slate-50 dark:bg-slate-700 rounded-t-lg overflow-hidden">
                <!-- Selection Checkbox -->
                <div class="absolute top-2 right-2 z-10" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isSelected(attachment)"
                    class="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    @change="toggleSelection(attachment, $event)"
                  />
                </div>
                <!-- Image Preview -->
                <img
                  v-if="isImage(attachment) && attachment.thumb_url"
                  :src="attachment.thumb_url"
                  :alt="attachment.file_name"
                  class="w-full h-full object-cover"
                />
                <!-- PDF Icon -->
                <div v-else-if="isPdf(attachment)" class="flex flex-col items-center justify-center">
                  <svg class="w-16 h-16 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.9,12.35 10.92,12.31Z"/>
                  </svg>
                </div>
                <!-- Video Icon -->
                <div v-else-if="isVideo(attachment)" class="flex flex-col items-center justify-center">
                  <span class="i-lucide-video text-5xl text-purple-500" />
                </div>
                <!-- Audio Icon with Player -->
                <div
                  v-else-if="isAudio(attachment)"
                  class="flex flex-col items-center justify-center cursor-pointer group"
                  @click.stop="toggleAudio(attachment)"
                >
                  <div class="relative">
                    <span
                      :class="isPlaying(attachment) ? 'i-lucide-pause-circle text-green-600' : 'i-lucide-play-circle text-green-500'"
                      class="text-6xl transition-all group-hover:scale-110"
                    />
                    <div
                      v-if="isPlaying(attachment)"
                      class="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
                    >
                      <div class="flex gap-0.5">
                        <span class="w-1 h-3 bg-green-500 rounded animate-pulse" />
                        <span class="w-1 h-4 bg-green-500 rounded animate-pulse delay-75" />
                        <span class="w-1 h-2 bg-green-500 rounded animate-pulse delay-150" />
                        <span class="w-1 h-4 bg-green-500 rounded animate-pulse delay-75" />
                        <span class="w-1 h-3 bg-green-500 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                  <span class="text-xs text-slate-500 mt-2">
                    {{ isPlaying(attachment) ? t('DOCUMENTS.PAUSE') : t('DOCUMENTS.PLAY') }}
                  </span>
                </div>
                <!-- Generic File Icon -->
                <div v-else class="flex flex-col items-center justify-center">
                  <span :class="[getFileIcon(attachment.file_type), getFileIconClass(attachment.file_type)]" class="text-5xl" />
                </div>

                <!-- Most Recent Badge -->
                <div
                  v-if="index === 0 && sentPage === 1"
                  class="absolute top-2 left-2 bg-green-700 text-white text-xs font-bold px-2 py-1 rounded shadow"
                >
                  {{ t('DOCUMENTS.MOST_RECENT') }}
                </div>
              </div>

              <!-- Info Area -->
              <div class="p-3">
                <!-- Phone -->
                <p class="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">
                  {{ contactPhone || '-' }}
                </p>
                <!-- Date -->
                <p class="text-xs text-slate-500">
                  {{ formatDate(attachment.created_at) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Received Section -->
        <div v-if="receivedAttachments.length > 0">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span class="i-lucide-arrow-down-left text-blue-500 text-xl" />
              <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">
                {{ t('DOCUMENTS.RECEIVED') }}
              </h3>
              <span class="text-sm text-slate-500">({{ receivedAttachments.length }})</span>
            </div>
            <!-- Pagination Controls -->
            <div v-if="receivedTotalPages > 1" class="flex items-center gap-2">
              <button
                :disabled="receivedPage === 1"
                class="px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700"
                @click="receivedPage--"
              >
                <span class="i-lucide-chevron-left" />
              </button>
              <span class="text-sm text-slate-600 dark:text-slate-400">
                {{ receivedPage }} / {{ receivedTotalPages }}
              </span>
              <button
                :disabled="receivedPage === receivedTotalPages"
                class="px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700"
                @click="receivedPage++"
              >
                <span class="i-lucide-chevron-right" />
              </button>
            </div>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <div
              v-for="(attachment, index) in paginatedReceived"
              :key="attachment.id"
              class="document-card bg-white dark:bg-slate-800 rounded-lg border-2 transition-all cursor-pointer hover:shadow-lg"
              :class="isSelected(attachment) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-blue-200 dark:border-blue-800 hover:border-blue-400'"
              @click="openAttachment(attachment)"
            >
              <!-- Preview Area -->
              <div class="relative aspect-square flex items-center justify-center bg-slate-50 dark:bg-slate-700 rounded-t-lg overflow-hidden">
                <!-- Selection Checkbox -->
                <div class="absolute top-2 right-2 z-10" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isSelected(attachment)"
                    class="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    @change="toggleSelection(attachment, $event)"
                  />
                </div>
                <!-- Image Preview -->
                <img
                  v-if="isImage(attachment) && attachment.thumb_url"
                  :src="attachment.thumb_url"
                  :alt="attachment.file_name"
                  class="w-full h-full object-cover"
                />
                <!-- PDF Icon -->
                <div v-else-if="isPdf(attachment)" class="flex flex-col items-center justify-center">
                  <svg class="w-16 h-16 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.9,12.35 10.92,12.31Z"/>
                  </svg>
                </div>
                <!-- Video Icon -->
                <div v-else-if="isVideo(attachment)" class="flex flex-col items-center justify-center">
                  <span class="i-lucide-video text-5xl text-purple-500" />
                </div>
                <!-- Audio Icon with Player -->
                <div
                  v-else-if="isAudio(attachment)"
                  class="flex flex-col items-center justify-center cursor-pointer group"
                  @click.stop="toggleAudio(attachment)"
                >
                  <div class="relative">
                    <span
                      :class="isPlaying(attachment) ? 'i-lucide-pause-circle text-green-600' : 'i-lucide-play-circle text-green-500'"
                      class="text-6xl transition-all group-hover:scale-110"
                    />
                    <div
                      v-if="isPlaying(attachment)"
                      class="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
                    >
                      <div class="flex gap-0.5">
                        <span class="w-1 h-3 bg-green-500 rounded animate-pulse" />
                        <span class="w-1 h-4 bg-green-500 rounded animate-pulse delay-75" />
                        <span class="w-1 h-2 bg-green-500 rounded animate-pulse delay-150" />
                        <span class="w-1 h-4 bg-green-500 rounded animate-pulse delay-75" />
                        <span class="w-1 h-3 bg-green-500 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                  <span class="text-xs text-slate-500 mt-2">
                    {{ isPlaying(attachment) ? t('DOCUMENTS.PAUSE') : t('DOCUMENTS.PLAY') }}
                  </span>
                </div>
                <!-- Generic File Icon -->
                <div v-else class="flex flex-col items-center justify-center">
                  <span :class="[getFileIcon(attachment.file_type), getFileIconClass(attachment.file_type)]" class="text-5xl" />
                </div>

                <!-- Most Recent Badge -->
                <div
                  v-if="index === 0 && receivedPage === 1"
                  class="absolute top-2 left-2 bg-green-700 text-white text-xs font-bold px-2 py-1 rounded shadow"
                >
                  {{ t('DOCUMENTS.MOST_RECENT') }}
                </div>
              </div>

              <!-- Info Area -->
              <div class="p-3">
                <!-- Phone -->
                <p class="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">
                  {{ contactPhone || '-' }}
                </p>
                <!-- Date -->
                <p class="text-xs text-slate-500">
                  {{ formatDate(attachment.created_at) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Section (fallback for attachments without valid message_type) -->
        <div v-if="otherAttachments.length > 0">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <span class="i-lucide-file text-slate-500 text-xl" />
              <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">
                {{ t('DOCUMENTS.OTHER') }}
              </h3>
              <span class="text-sm text-slate-500">({{ otherAttachments.length }})</span>
            </div>
            <!-- Pagination Controls -->
            <div v-if="otherTotalPages > 1" class="flex items-center gap-2">
              <button
                :disabled="otherPage === 1"
                class="px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700"
                @click="otherPage--"
              >
                <span class="i-lucide-chevron-left" />
              </button>
              <span class="text-sm text-slate-600 dark:text-slate-400">
                {{ otherPage }} / {{ otherTotalPages }}
              </span>
              <button
                :disabled="otherPage === otherTotalPages"
                class="px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700"
                @click="otherPage++"
              >
                <span class="i-lucide-chevron-right" />
              </button>
            </div>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <div
              v-for="(attachment, index) in paginatedOther"
              :key="attachment.id"
              class="document-card bg-white dark:bg-slate-800 rounded-lg border-2 transition-all cursor-pointer hover:shadow-lg"
              :class="isSelected(attachment) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 dark:border-slate-700 hover:border-slate-400'"
              @click="openAttachment(attachment)"
            >
              <!-- Preview Area -->
              <div class="relative aspect-square flex items-center justify-center bg-slate-50 dark:bg-slate-700 rounded-t-lg overflow-hidden">
                <!-- Selection Checkbox -->
                <div class="absolute top-2 right-2 z-10" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isSelected(attachment)"
                    class="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    @change="toggleSelection(attachment, $event)"
                  />
                </div>
                <!-- Image Preview -->
                <img
                  v-if="isImage(attachment) && attachment.thumb_url"
                  :src="attachment.thumb_url"
                  :alt="attachment.file_name"
                  class="w-full h-full object-cover"
                />
                <!-- PDF Icon -->
                <div v-else-if="isPdf(attachment)" class="flex flex-col items-center justify-center">
                  <svg class="w-16 h-16 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.9,12.35 10.92,12.31Z"/>
                  </svg>
                </div>
                <!-- Video Icon -->
                <div v-else-if="isVideo(attachment)" class="flex flex-col items-center justify-center">
                  <span class="i-lucide-video text-5xl text-purple-500" />
                </div>
                <!-- Audio Icon with Player -->
                <div
                  v-else-if="isAudio(attachment)"
                  class="flex flex-col items-center justify-center cursor-pointer group"
                  @click.stop="toggleAudio(attachment)"
                >
                  <div class="relative">
                    <span
                      :class="isPlaying(attachment) ? 'i-lucide-pause-circle text-green-600' : 'i-lucide-play-circle text-green-500'"
                      class="text-6xl transition-all group-hover:scale-110"
                    />
                    <div
                      v-if="isPlaying(attachment)"
                      class="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
                    >
                      <div class="flex gap-0.5">
                        <span class="w-1 h-3 bg-green-500 rounded animate-pulse" />
                        <span class="w-1 h-4 bg-green-500 rounded animate-pulse delay-75" />
                        <span class="w-1 h-2 bg-green-500 rounded animate-pulse delay-150" />
                        <span class="w-1 h-4 bg-green-500 rounded animate-pulse delay-75" />
                        <span class="w-1 h-3 bg-green-500 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                  <span class="text-xs text-slate-500 mt-2">
                    {{ isPlaying(attachment) ? t('DOCUMENTS.PAUSE') : t('DOCUMENTS.PLAY') }}
                  </span>
                </div>
                <!-- Generic File Icon -->
                <div v-else class="flex flex-col items-center justify-center">
                  <span :class="[getFileIcon(attachment.file_type), getFileIconClass(attachment.file_type)]" class="text-5xl" />
                </div>

                <!-- Most Recent Badge -->
                <div
                  v-if="index === 0 && otherPage === 1"
                  class="absolute top-2 left-2 bg-green-700 text-white text-xs font-bold px-2 py-1 rounded shadow"
                >
                  {{ t('DOCUMENTS.MOST_RECENT') }}
                </div>
              </div>

              <!-- Info Area -->
              <div class="p-3">
                <!-- Phone -->
                <p class="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">
                  {{ contactPhone || '-' }}
                </p>
                <!-- Date -->
                <p class="text-xs text-slate-500">
                  {{ formatDate(attachment.created_at) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.documents-tab {
  min-height: 400px;
}

.document-card {
  transition: all 0.2s ease;
}

.document-card:hover {
  transform: translateY(-2px);
}

/* Download ZIP button - explicit colors for both themes */
.download-zip-btn {
  background-color: #2563eb !important; /* blue-600 */
  color: #ffffff !important;
  border: 1px solid #1d4ed8; /* blue-700 */
}

.download-zip-btn:hover:not(:disabled) {
  background-color: #1d4ed8 !important; /* blue-700 */
}

.download-zip-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
